import { Constants } from "./constants";

/**
 * Dog Profile / Ribbon Room access rules shared by web and mobile.
 *
 * Pure functions with structural parameter types so this module has no
 * dependency on `@eagerdog/interfaces` (which depends on this package).
 */

/**
 * Dog Owner Premium is retired (Nov 2026): Ribbon Room, Stats, Run History,
 * manual runs, tracker settings and past event results are free for every
 * owner. The paywall code (PremiumGate, PremiumScreen, /premium owner plans,
 * subscription flag writes in the Stripe / RevenueCat webhooks, checkout for
 * owner tariff plans) is kept behind this switch so Premium can be
 * re-introduced later. See data/prd/TITLE_TRACKER_PAYWALL_REMOVAL_PRD.md §4.9
 * for the re-enable playbook before flipping this to true.
 */
export const DOG_OWNER_PREMIUM_ENABLED = false;

export interface IDogProfileAccessUser {
  _id?: string;
  user_type?: string;
  has_dog_profile_access?: boolean;
}

export interface IDogProfileAccessDog {
  owner_id?: string;
  created_by?: string;
  is_public?: boolean;
}

/** Owner of the dog, or a platform admin who is treated as an owner. */
export const isDogProfileOwner = (
  user: IDogProfileAccessUser | null | undefined,
  dog: IDogProfileAccessDog | null | undefined
): boolean => {
  if (!user || !dog) return false;
  const dogOwnerId = (dog.owner_id ?? dog.created_by)?.toString();
  const userId = user._id?.toString();
  return (
    (!!dogOwnerId && dogOwnerId === userId) ||
    user.user_type === Constants.user_type.eagerdog_admin ||
    // system_admin is not part of Constants.user_type; matches the web page's check
    user.user_type === "system_admin"
  );
};

/** Owners need the Dog Profile feature flag on their account to open their own dogs. */
export const canOpenOwnDogProfile = (user: IDogProfileAccessUser | null | undefined): boolean =>
  !!user?.has_dog_profile_access;

/**
 * Can this (possibly anonymous) user view the dog's profile?
 * Owners: only with feature access. Everyone else: only if the dog is public.
 */
export const canViewDogProfile = (
  user: IDogProfileAccessUser | null | undefined,
  dog: IDogProfileAccessDog | null | undefined
): boolean => {
  if (!dog) return false;
  if (isDogProfileOwner(user, dog)) {
    return canOpenOwnDogProfile(user);
  }
  return !!dog.is_public;
};
